import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  UsersThree,
  ShieldCheck,
  Warning,
  XCircle,
  ArrowRight,
  Sparkle,
  ChartLineUp,
  RocketLaunch,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import AddStaffDialog from "@/components/AddStaffDialog";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from "recharts";

const KpiCard = ({ label, value, icon: Icon, accent, testId, hint }) => (
  <div
    data-testid={testId}
    className="card-lift rounded-2xl border border-white/10 bg-slate-900 p-5 md:p-6 relative overflow-hidden"
  >
    <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full blur-2xl ${accent}`} />
    <div className="flex items-start justify-between mb-4 relative">
      <div className="font-label text-slate-400">{label}</div>
      <div className={`w-9 h-9 rounded-lg grid place-items-center ${accent}`}>
        <Icon size={18} weight="bold" className="text-slate-50" />
      </div>
    </div>
    <div className="kpi-number text-slate-50 relative">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-2 relative">{hint}</div>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [staff, setStaff] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [onboarding, setOnboarding] = useState(null);

  const load = async () => {
    const [s, st, al, ob] = await Promise.all([
      api.get("/dashboard/stats"),
      api.get("/staff-with-status"),
      api.get("/alerts"),
      api.get("/onboarding/status"),
    ]);
    setStats(s.data);
    setStaff(st.data);
    setAlerts(al.data);
    setOnboarding(ob.data);
  };

  useEffect(() => { load(); }, []);

  if (!stats) return <div className="p-8 text-slate-400">Loading dashboard…</div>;

  const chartData = [
    { name: "Current", value: stats.current, color: "#10b981" },
    { name: "Expiring", value: stats.expiring_soon, color: "#f59e0b" },
    { name: "Expired", value: stats.expired, color: "#f43f5e" },
  ].filter((d) => d.value > 0);

  const criticalStaff = staff.filter((s) => s.overall_status !== "current").slice(0, 6);

  return (
    <div className="p-4 md:p-8 max-w-[1600px] mx-auto">
      {/* Onboarding banner */}
      {onboarding && !onboarding.completed && !onboarding.skipped && (
        <Link
          to="/onboarding"
          data-testid="onboarding-banner"
          className="block mb-6 rounded-2xl border border-orange-500/30 bg-gradient-to-r from-orange-500/10 to-orange-500/5 p-5 hover:from-orange-500/15 transition-colors"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <RocketLaunch size={22} weight="bold" className="text-orange-400 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-slate-100">Finish setting up AuditReady Sports</div>
                <div className="text-xs text-slate-400 mt-1">{onboarding.progress_pct}% complete · resume the guided setup and download your first audit-ready report</div>
              </div>
            </div>
            <ArrowRight size={16} weight="bold" className="text-orange-400 shrink-0" />
          </div>
        </Link>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="font-label text-orange-400 mb-2">DASHBOARD · {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
          <h1 className="text-4xl md:text-5xl font-display font-black tracking-tight text-slate-50">
            Welcome, {user?.name?.split(" ")[0] || "Coach"}.
          </h1>
          <p className="text-slate-400 mt-2">Your audit-readiness snapshot — every credential, every staff member, every deadline.</p>
        </div>
        <Button
          data-testid="add-staff-button"
          onClick={() => setAddOpen(true)}
          className="bg-orange-500 text-orange-50 hover:bg-orange-400 font-semibold rounded-full px-5 h-11"
        >
          <Sparkle size={16} weight="bold" className="mr-2" />
          Add Staff
        </Button>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
        <KpiCard
          testId="kpi-total-staff"
          label="TOTAL STAFF"
          value={stats.total_staff}
          icon={UsersThree}
          accent="bg-slate-700"
          hint={`${stats.total_credentials} credentials on file`}
        />
        <KpiCard
          testId="kpi-current"
          label="CREDENTIALS CURRENT"
          value={stats.current}
          icon={ShieldCheck}
          accent="bg-emerald-600"
          hint={`${stats.compliance_rate}% compliance rate`}
        />
        <KpiCard
          testId="kpi-expiring"
          label="EXPIRING SOON"
          value={stats.expiring_soon}
          icon={Warning}
          accent="bg-amber-600"
          hint={`${stats.buckets["30"]} within 30 days`}
        />
        <KpiCard
          testId="kpi-expired"
          label="EXPIRED"
          value={stats.expired}
          icon={XCircle}
          accent="bg-rose-600"
          hint="Requires immediate action"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Alerts panel */}
        <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-slate-900 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="font-label text-slate-400 mb-1">ATTENTION QUEUE</div>
              <h2 className="text-2xl font-display font-black tracking-tight">Staff needing action</h2>
            </div>
            <Link to="/alerts" data-testid="view-all-alerts-link" className="text-sm text-orange-400 hover:text-orange-300 font-semibold inline-flex items-center gap-1.5">
              View all <ArrowRight size={14} weight="bold" />
            </Link>
          </div>

          {criticalStaff.length === 0 ? (
            <div className="text-center py-12">
              <ShieldCheck size={44} weight="duotone" className="text-orange-400 mx-auto mb-3" />
              <div className="text-slate-100 font-semibold">All credentials are current</div>
              <div className="text-slate-500 text-sm mt-1">Your team is fully compliant. Nice work.</div>
            </div>
          ) : (
            <div className="space-y-2">
              {criticalStaff.map((s) => (
                <Link
                  key={s.id}
                  to={`/staff/${s.id}`}
                  data-testid={`critical-staff-${s.id}`}
                  className="flex items-center justify-between p-4 rounded-xl border border-white/5 bg-slate-950/60 hover:border-white/15 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-slate-800 grid place-items-center text-xs font-bold text-slate-300 shrink-0">
                      {s.name.split(" ").map(n => n[0]).slice(0,2).join("")}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-100 truncate">{s.name}</div>
                      <div className="text-xs text-slate-500 capitalize">{s.role} · {s.team || "—"}</div>
                    </div>
                  </div>
                  <StatusBadge status={s.overall_status} color={s.status_color} />
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Status donut */}
        <div className="rounded-2xl border border-white/10 bg-slate-900 p-6">
          <div className="font-label text-slate-400 mb-1">CREDENTIAL STATUS</div>
          <h2 className="text-2xl font-display font-black tracking-tight mb-6">Portfolio breakdown</h2>

          {chartData.length === 0 ? (
            <div className="text-slate-500 text-sm">No credentials yet.</div>
          ) : (
            <>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={chartData} innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value">
                      {chartData.map((d, i) => <Cell key={i} fill={d.color} stroke="none" />)}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#f1f5f9" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2 mt-3">
                {chartData.map((d) => (
                  <div key={d.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                      <span className="text-slate-300">{d.name}</span>
                    </div>
                    <span className="font-semibold text-slate-100">{d.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Expiration buckets */}
      <div className="mt-8 rounded-2xl border border-white/10 bg-slate-900 p-6">
        <div className="flex items-center gap-2 mb-6">
          <ChartLineUp size={20} className="text-orange-400" weight="bold" />
          <h2 className="text-xl font-display font-black tracking-tight">Expiration Timeline</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: "WITHIN 90 DAYS", count: stats.buckets["90"], color: "border-amber-500/25 bg-amber-500/5 text-amber-300" },
            { label: "WITHIN 60 DAYS", count: stats.buckets["60"], color: "border-orange-500/25 bg-orange-500/5 text-orange-300" },
            { label: "WITHIN 30 DAYS", count: stats.buckets["30"], color: "border-rose-500/25 bg-rose-500/5 text-rose-300" },
          ].map((b, i) => (
            <div key={i} className={`rounded-xl border p-5 ${b.color}`}>
              <div className="font-label mb-3 opacity-80">{b.label}</div>
              <div className="kpi-number">{b.count}</div>
              <div className="text-xs mt-2 opacity-75">credentials at risk</div>
            </div>
          ))}
        </div>
      </div>

      <AddStaffDialog open={addOpen} onOpenChange={setAddOpen} onCreated={load} />
    </div>
  );
}
